import React, { useRef, useState, useEffect } from "react";

export type LogoUploadModalProps = {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  primaryColor?: string;
};

const LogoUploadModal: React.FC<LogoUploadModalProps> = ({
  open,
  onClose,
  onUpload,
  primaryColor = "#2076D1",
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (open) {
      // Reset state whenever modal is opened
      setSelectedFile(null);
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = () => {
    if (selectedFile) {
      onUpload(selectedFile);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-transparent bg-opacity-30 backdrop-blur-xs">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 relative">
        <h2 className="text-lg font-semibold mb-4">Institute Logo</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Upload Logo</label>
          <div
            className={`border-2 border-dashed rounded-lg flex flex-col items-center justify-center p-6 cursor-pointer transition-colors ${
              dragActive
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 bg-gray-50"
            }`}
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
              <svg
                width="32"
                height="32"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="mb-2 text-gray-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                />
              </svg>
              <span className="font-medium text-gray-700">
                {selectedFile ? "File selected" : "Click to upload"}
              </span>{" "}
              <span className="text-gray-500">or drag and drop</span>
              <div className="text-xs text-gray-400 mt-1">
                PNG or JPEG, (max. 512×512px)
              </div>
              <input
                type="file"
                accept="image/png, image/jpeg"
                className="hidden"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              {selectedFile && (
                <div className="mt-2 text-xs text-green-600">
                  Selected: {selectedFile.name}
                </div>
              )}
            </div>
        </div>
        <div className="flex gap-4 justify-end mt-6">
          <button
            type="button"
            style={{
              color: primaryColor,
              borderColor: primaryColor,
              backgroundColor: "#fff",
            }}
            className="rounded px-6 py-2 font-semibold border hover:bg-blue-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            style={{
              backgroundColor: primaryColor,
              color: "#fff",
              borderColor: primaryColor,
            }}
            className="rounded px-6 py-2 font-semibold border hover:opacity-90"
            onClick={handleSave}
            disabled={!selectedFile}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoUploadModal;
